import { Grid } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useContactpersoonMutaties } from '../../api/klanten.ts';
import type { Contactpersoon } from '../../api/typen.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { Tekstveld } from '../../componenten/velden.tsx';
import { leegNaarNull } from '../../helpers/formulier.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

interface Waarden {
  voornaam: string;
  achternaam: string;
  functie: string;
  email: string;
  telefoon: string;
  linkedinUrl: string;
}

interface Props {
  open: boolean;
  onSluit: () => void;
  bedrijfId: string;
  bestaand?: Contactpersoon | undefined;
}

function beginwaarden(bestaand: Contactpersoon | undefined): Waarden {
  return {
    voornaam: bestaand?.voornaam ?? '',
    achternaam: bestaand?.achternaam ?? '',
    functie: bestaand?.functie ?? '',
    email: bestaand?.email ?? '',
    telefoon: bestaand?.telefoon ?? '',
    linkedinUrl: bestaand?.linkedinUrl ?? '',
  };
}

/**
 * Voegt een contactpersoon toe aan een bedrijf of wijzigt hem.
 * @param props Het bedrijf en de bestaande contactpersoon om te wijzigen.
 * @returns De dialoog.
 */
export function ContactpersoonFormulier({ open, onSluit, bedrijfId, bestaand }: Props) {
  const mutaties = useContactpersoonMutaties();
  const formulier = useForm<Waarden>({ defaultValues: beginwaarden(bestaand) });
  useEffect(() => {
    if (open) {
      formulier.reset(beginwaarden(bestaand));
    }
  }, [open, bestaand, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) => {
      const invoer = {
        voornaam: waarden.voornaam,
        achternaam: waarden.achternaam,
        functie: leegNaarNull(waarden.functie),
        email: leegNaarNull(waarden.email),
        telefoon: leegNaarNull(waarden.telefoon),
        linkedinUrl: leegNaarNull(waarden.linkedinUrl),
      };
      return bestaand
        ? mutaties.wijzig.mutateAsync({ id: bestaand.id, invoer })
        : mutaties.voegToe.mutateAsync({ bedrijfId, invoer });
    },
    { melding: bestaand ? 'Contactpersoon gewijzigd' : 'Contactpersoon toegevoegd', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Contactpersoon wijzigen' : 'Nieuwe contactpersoon'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="voornaam" label="Voornaam" verplicht autoFocus />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="achternaam" label="Achternaam" verplicht />
          </Grid>
          <Grid size={12}>
            <Tekstveld naam="functie" label="Functie" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="email" label="E-mailadres" type="email" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="telefoon" label="Telefoon" type="tel" />
          </Grid>
          <Grid size={12}>
            <Tekstveld naam="linkedinUrl" label="LinkedIn-profiel" type="url" />
          </Grid>
        </Grid>
      </FormulierDialoog>
    </FormProvider>
  );
}
